import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clapperboard, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ReelEditRow, ReelRow } from './ReelRows';
import { useAuthStore } from '../store';
import { reelsBankAPI } from '../lib/api';

const COLS = ['Sector', 'Cliente', 'Gancho', 'Fórmula', 'Formato', 'URL', 'Comentarios', 'Estado', 'Fecha añadido', ''];
const sel = 'h-10 rounded-md border px-2 text-sm bg-background';

export default function ReelsBankPage() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('');
  const [cliente, setCliente] = useState('');
  const [estado, setEstado] = useState('');
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => reelsBankAPI.list().then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { if (user?.role === 'ceo') load(); }, [load, user?.role]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => (!sector || r.sector === sector) && (!estado || r.estado === estado)
      && (!cliente || (cliente === '__none' ? !r.cliente_id : r.cliente_id === cliente))
      && (!s || [r.gancho, r.formula, r.formato, r.comentarios, r.usado_en, r.cliente_nombre, r.sector].some((v) => (v || '').toLowerCase().includes(s))));
  }, [data, q, sector, cliente, estado]);

  if (user && user.role !== 'ceo') return <Navigate to="/resources" replace />;

  const addSector = async (n) => { await reelsBankAPI.addSector(n); setData((d) => ({ ...d, sectors: [...new Set([...d.sectors, n])].sort((a, b) => a.localeCompare(b, 'es')) })); };
  const create = async (f) => {
    try { const r = await reelsBankAPI.create(f); setData((d) => ({ ...d, rows: [r, ...d.rows] })); setEditing(null); toast.success('Referencia añadida'); }
    catch (e) { toast.error(e.message); }
  };
  const update = async (id, f) => {
    try { const r = await reelsBankAPI.update(id, f); setData((d) => ({ ...d, rows: d.rows.map((x) => (x.id === id ? r : x)) })); setEditing(null); toast.success('Guardado'); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (r) => {
    if (!window.confirm('¿Eliminar esta referencia?')) return;
    try { await reelsBankAPI.remove(r.id); setData((d) => ({ ...d, rows: d.rows.filter((x) => x.id !== r.id) })); toast.success('Referencia eliminada'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="reels-bank-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/resources?tab=direction" className="text-muted-foreground hover:text-foreground" data-testid="reels-back"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold flex items-center gap-2"><Clapperboard className="w-7 h-7 text-primary" />Banco de reels y TikToks virales</h1>
          <p className="text-sm text-muted-foreground">Referencias para adaptar · Recursos de dirección (solo CEOs)</p>
        </div>
        <Button onClick={() => setEditing('new')} disabled={editing === 'new'} data-testid="reels-add-btn"><Plus className="w-4 h-4 mr-1" />Añadir referencia</Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en gancho, fórmula, formato, comentarios…" className="pl-9" data-testid="reels-search" />
        </div>
        <select value={sector} onChange={(e) => setSector(e.target.value)} className={sel} data-testid="reels-filter-sector">
          <option value="">Todos los sectores</option>
          {(data?.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={cliente} onChange={(e) => setCliente(e.target.value)} className={sel} data-testid="reels-filter-cliente">
          <option value="">Todos los clientes</option><option value="__none">Sin cliente</option>
          {(data?.clients || []).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} className={sel} data-testid="reels-filter-estado">
          <option value="">Todos los estados</option>
          {(data?.estados || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {data && <span className="text-xs text-muted-foreground" data-testid="reels-count">{rows.length} de {data.rows.length}</span>}
      </div>
      {!data ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <table className="w-full text-left" data-testid="reels-table">
            <thead className="bg-muted/50">
              <tr>{COLS.map((c) => <th key={c} className="p-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{c}</th>)}</tr>
            </thead>
            <tbody>
              {editing === 'new' && <ReelEditRow initial={{ sector, cliente_id: cliente && cliente !== '__none' ? cliente : '' }} meta={data} onAddSector={addSector} onSave={create} onCancel={() => setEditing(null)} testId="reel-new" />}
              {rows.map((r) => (editing === r.id
                ? <ReelEditRow key={r.id} initial={r} meta={data} onAddSector={addSector} onSave={(f) => update(r.id, f)} onCancel={() => setEditing(null)} testId={`reel-edit-row-${r.id}`} />
                : <ReelRow key={r.id} r={r} onEdit={() => setEditing(r.id)} onDelete={() => remove(r)} />))}
              {!rows.length && editing !== 'new' && <tr><td colSpan={COLS.length} className="p-10 text-center text-sm text-muted-foreground">No hay referencias con estos filtros.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
